<template>
  <div class="px-2 lg:px-4 py-2">
    <PBreadcrumb :parts="parts" current-page-label="Guide d'utilisation" />
    <div class="flex justify-evenly w-full py-6">
      <div class="flex flex-col items-start gap-5 max-w-2xl">
        <h1 class="text-h1 font-bold">Guide d'utilisation</h1>
        <div>
          <div class="text-xl">
            <p>
              Des tutoriels ainsi que des liens utiles sont à votre disposition afin de prendre en main le parapheur.
            </p>
          </div>
          <div class="flex gap-2 mt-3">
            <PButton :to="config.tutoPdfLink" target="_blank">
              <span> Télécharger le document </span>
              <OhVueIcon :name="dlIcon" class="ml-1" />
            </PButton>
            <PButton mode="secondary" @click="userPreferencesStore.onboardingSeen = false">
              <span> Accéder au tutoriel par étape </span>
            </PButton>
          </div>
        </div>
        <div>
          <h2 class="text-h2 font-bold mb-2">Liens utiles</h2>
          <ul class="flex flex-col gap-2">
            <li>
              <PLink to="/sign-help">
                <span> Accéder à la page d'aide à la signature renforcée </span>
                <OhVueIcon :name="rightArrowIcon" class="ml-1v" />
              </PLink>
            </li>
            <li>
              <PLink v-if="config.goodpracticesLink" :to="config.goodpracticesLink">
                <span> Consulter les bonnes pratiques dans les services </span>
              </PLink>
            </li>
            <li>
              <PLink
                v-if="config.graphicsChartLink"
                :to="config.graphicsChartLink"
                detail="L'accès aux modèles n'est possible que depuis l'intranet."
              >
                <span> Consulter la charte graphique de l'État et les modèles de documents </span>
              </PLink>
            </li>
          </ul>
        </div>
        <div>
          <h2 class="text-h2 font-bold mb-3">Une question ?</h2>
          <p class="mb-3">
            L’équipe projet est à votre disposition via le canal Tchap ou par courriel (mél) :
            {{ config.contact }}
          </p>
          <div class="flex gap-2">
            <PButton :to="config.tchapLink" target="_blank" mode="secondary">
              <span> Accéder au canal Tchap </span>
              <OhVueIcon :name="linkIcon" class="ml-1" />
            </PButton>
            <PButton @click="copyMail" mode="tertiary">
              <span>Copier l'adresse mél</span>
              <OhVueIcon :name="emailIcon" class="ml-1" />
            </PButton>
          </div>
        </div>
        <div class="bg-blue-france-950 p-4 flex flex-col items-center gap-2">
          <h2 class="text-h2 font-bold">Aidez-nous à améliorer ce service !</h2>
          <p>Donnez-nous votre avis, cela ne prend que 2 minutes.</p>
          <a
            class="ml-2 mr-1 h-[2.6rem]"
            href="https://jedonnemonavis.numerique.gouv.fr/Demarches/3669?&view-mode=formulaire-avis&nd_source=button&key=ab6f3e67e351370b309b454860b7916a"
            target="_blank"
            data-sentry-id="AppFeedbackLink"
          >
            <img
              src="https://jedonnemonavis.numerique.gouv.fr/static/bouton-blanc.svg"
              alt="Je donne mon avis"
              class="h-full"
            />
          </a>
        </div>
      </div>
      <div class="hidden xl:block mt-4">
        <div class="w-[15rem] h-[15rem] bg-grey-975 rounded-full -skew-x-6 p-6">
          <div
            class="bg-no-repeat bg-cover bg-center w-full h-full skew-x-6"
            :style="`background-image: url(/info.svg);`"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  PBreadcrumb,
  PButton,
  useAlertManager,
  AlertType,
  AlertDuration,
  useConfig,
  useIcons,
  PLink,
} from "@pasta/box-v2";
import { type ParapheurConfig } from "../types";
import { RiMailLine, RiDownloadLine, RiArrowRightLine, RiArrowRightUpLine } from "oh-vue-icons/icons";
import { OhVueIcon } from "oh-vue-icons";
import { useUserPreferencesStore } from "../stores/user-preferences.store";

const parts = [{ label: "Accueil", to: "/" }];

const userPreferencesStore = useUserPreferencesStore();
const config = useConfig<ParapheurConfig>();
const alertManager = useAlertManager();
const [emailIcon, dlIcon, rightArrowIcon, linkIcon] = useIcons(
  RiMailLine,
  RiDownloadLine,
  RiArrowRightLine,
  RiArrowRightUpLine
);

function copyMail() {
  alertManager.createAlert({
    type: AlertType.INFO,
    duration: AlertDuration.SHORT,
    message: "Adresse email copiée",
  });
  navigator.clipboard.writeText(config.contact || "");
}
</script>
